/**
 * ATL-970 — the boot-time Supabase load, with "empty" and "failed" kept apart.
 *
 * The old load was `Promise.all` over seven fetchers that could not fail (each swallowed to `{}`),
 * followed by a single emptiness test on `stores`. A failed products fetch therefore produced an
 * empty catalog, the log line `✅ Supabase data loaded successfully`, and no error of any kind —
 * a till the cashier could not sell from, that looked legitimately empty.
 *
 * The fetchers now throw (see `utils/supabaseFetchError`), so this module can ask the question the
 * old code could not: *did this collection fail, or is it genuinely empty?*
 *
 * ## Why a loud refusal rather than a degraded till
 *
 * Measured before choosing (Sentry, project `qarasay/qarasay-kassa`, tag `supabase_fetch_error`):
 * 116 events between 2026-06-11 and 2026-08-14 across the whole fleet, of which the boot-critical
 * collections account for `products` 9 (last 2026-06-16) and `stores` 8 (last 2026-06-11). On the
 * collections below, a failure is close to never — so refusing costs close to nothing, and it is
 * the same call ATL-968 made server-side when it chose to fail the catalog request rather than
 * return a truncated catalog. This is accounting software: a visible refusal the merchant can act
 * on beats a silent partial catalog they cannot.
 *
 * The refusal is not permanent — the caller shows a "restart the app" toast, and a restart retries.
 */

import { SupabaseFetchError } from '../../utils/supabaseFetchError'
import { withTimeout } from '../../utils/withTimeout'

/**
 * Whether a FAILED load of each collection should stop the till.
 *
 * `critical: true` means the app cannot honestly present itself as working without it.
 * `critical: false` means the till still functions; the caller keeps its previous copy of the data
 * and tells the merchant which part is stale.
 */
export const COLLECTION_CRITICALITY = Object.freeze({
  // Nothing can be sold from a catalog we could not read.
  products: { critical: true },
  // The catalog is presented grouped by category; with none, `getProductsByCategoryList` emits
  // sections with `title: undefined` in arbitrary order — indistinguishable from a corrupt catalog.
  productCategories: { critical: true },
  // Every read and write is store-scoped, and a store is created with the organization, so this is
  // the one collection where EMPTY is also fatal (see `emptyIsFatal`).
  stores: { critical: true, emptyIsFatal: true },
  // Payment methods, wallets, roles, price types. Without them a sale cannot be completed and the
  // user's permissions fall back to a fabricated role.
  constants: { critical: true },
  // Carries the Supabase UUID every realtime subscription filters on; without it the app runs with
  // no live updates at all and never says so.
  organization: { critical: true },
  // The staff list. The signed-in cashier comes from Firebase Auth, not from here, so a sale can
  // still be rung and attributed; only staff pickers and admin screens are affected.
  users: { critical: false },
  // Needed to record a supply, never to make a sale.
  suppliers: { critical: false },
  // The merchant's custom invoice layouts. A sale can still be rung and a receipt still prints —
  // it prints with the BUILT-IN layout instead of theirs, which is why this is not critical but is
  // also not nothing: `getPrintTemplateSettings()` returns `null` when the atom is empty and
  // `generateExpenseInvoice` then drops the logo, the extra image and every configured
  // `includeFields`. Loading it here is the whole point of this entry — in Firestore mode
  // `AppStack` hydrates `printTemplatesAtom` from a live `printTemplates` listener, and the
  // Supabase path had NO equivalent: the subscription factory only ever CLEARED the atom
  // (`clearOnly`) and the one fetcher that existed was never called. So a cut-over till fell back
  // to the default invoice silently — the same shape as ATL-1038's `printChecks`, where the row
  // was present in both stores and the READ PATH simply did not return it.
  printTemplates: { critical: false },
})

/** Thrown when at least one collection the till cannot work without failed to load. */
export class SupabaseInitialLoadError extends Error {
  /**
   * @param {Array<{collection: string, critical: boolean, error: Error}>} failures every collection
   *   that failed — critical or not. All of them, not just the one that stopped the boot: a single
   *   root cause usually breaks several at once and reporting one hides the shape.
   */
  constructor(failures) {
    const criticalCollections = failures.filter((f) => f.critical).map((f) => f.collection)
    super(`Failed to load critical data from Supabase: ${criticalCollections.join(', ')}`)
    this.name = 'SupabaseInitialLoadError'
    this.failures = failures
    this.criticalCollections = criticalCollections
  }
}

/** A collection that resolved to nothing where nothing is not a real state. */
class EmptyCollectionError extends Error {
  constructor(collection) {
    super(`Supabase returned no ${collection} for this organization`)
    this.name = 'EmptyCollectionError'
    this.collection = collection
  }
}

const isEmpty = (value) => !value || Object.keys(value).length === 0

/**
 * Load the collections the app needs before it can open the till.
 *
 * @param {Object} args
 * @param {Record<string, () => Promise<unknown>>} args.fetchers one thunk per collection; the keys
 *   must be those of `COLLECTION_CRITICALITY`.
 * @param {number} [args.timeoutMs] per-collection budget. Per-collection rather than one shared
 *   budget for the whole load: they run concurrently so the wall-clock ceiling is unchanged, but a
 *   timeout is now attributable to the collection that caused it instead of to "the load".
 * @returns {Promise<{data: Record<string, unknown>, degraded: Array<{collection: string, error: Error}>}>}
 *   `data` holds a key ONLY for a collection that loaded. A failed non-critical collection is absent
 *   rather than empty, so the caller can leave its atom untouched and keep the last good copy —
 *   writing `{}` over it is the same defect one screen further along.
 * @throws {SupabaseInitialLoadError} if any critical collection failed.
 */
export async function loadInitialSupabaseData({ fetchers, timeoutMs = 30000 }) {
  const names = Object.keys(fetchers)
  // ⚠️ NOT `Promise.allSettled`. React Native installs its own Promise over Hermes
  // (`promise/setimmediate/es6-extensions` + `finally` + rejection-tracking), and that polyfill
  // provides `all`, `race`, `resolve`, `reject` and `finally` — but NOT `allSettled`, which is
  // ES2020. This module was the only caller of it in the whole app, so nothing had ever proved it
  // existed; on device it is `undefined`, and calling it throws `TypeError: undefined is not a
  // function` INSIDE the very try/catch ATL-970 had just made fatal. Every native user of a
  // Supabase-reading org got "Ошибка загрузки данных" and an unusable till: 130 events / 38 users
  // in the 27h after the v7.0.101 OTA (Sentry QARASAY-KASSA-2W8).
  //
  // Browser QA cannot catch this — every browser has `allSettled`. `initialLoadWithoutAllSettled`
  // deletes the global to reproduce the device.
  //
  // `.then(onFulfilled, onRejected)` is the settle primitive every Promise implementation has, so
  // this reproduces allSettled's contract without depending on it. Attaching the handler at
  // creation ALSO means no fetcher rejection can go unhandled, which is what turned a single
  // TypeError into a crash rather than a caught error.
  const settled = await Promise.all(
    names.map((name) => {
      const criticality = COLLECTION_CRITICALITY[name]
      return withTimeout(
        Promise.resolve().then(fetchers[name]),
        timeoutMs,
        () => new SupabaseFetchError(name, new Error(`timed out after ${timeoutMs}ms`))
      )
        .then((value) => {
          if (criticality?.emptyIsFatal && isEmpty(value)) throw new EmptyCollectionError(name)
          return value
        })
        .then(
          (value) => ({ status: 'fulfilled', value }),
          (reason) => ({ status: 'rejected', reason }),
        )
    })
  )

  const data = {}
  const failures = []
  settled.forEach((outcome, index) => {
    const collection = names[index]
    if (outcome.status === 'fulfilled') {
      data[collection] = outcome.value
      return
    }
    failures.push({
      collection,
      critical: COLLECTION_CRITICALITY[collection]?.critical ?? true,
      error: outcome.reason,
    })
  })

  if (failures.some((f) => f.critical)) throw new SupabaseInitialLoadError(failures)

  return { data, degraded: failures.map(({ collection, error }) => ({ collection, error })) }
}

export default loadInitialSupabaseData
